/**
 * HybridCache Service
 *
 * Intelligent caching system using S3 for batch content and DynamoDB for client content.
 * Implements TTL management, cache invalidation, and performance optimization.
 * Part of Week 2 Day 2 implementation for US-029.
 */

import logger from '../utils/logger.js';
import {
  createNetworkError,
  createCacheError,
  isErrorResponse,
  logError
} from '../utils/StandardErrorFormat.js';

// Note: AWS SDK would need to be accessed via API endpoints in browser
// For Node.js backend, you'd use: import AWS from 'aws-sdk';
const s3 = null; // Will be replaced with API calls
const dynamodb = null; // Will be replaced with API calls
const cloudwatch = null; // Will be replaced with API calls

class HybridCache {
    constructor(options = {}) {
    // Create service-specific logger
    this.logger = logger.child('HybridCache');

        // S3 configuration for batch content
        this.s3Bucket = options.s3Bucket || 'financial-newsletter-data';
        this.batchPrefix = options.batchPrefix || 'cache/batch/';
        
        // DynamoDB configuration for client content
        this.clientCacheTable = options.clientCacheTable || 'ClientContentCache';
        
        // TTL configurations (in milliseconds)
        this.ttl = {
            batch: options.batchTTL || 6 * 60 * 60 * 1000,     // 6 hours
            client: options.clientTTL || 24 * 60 * 60 * 1000,  // 24 hours
            market: options.marketTTL || 15 * 60 * 1000,       // 15 minutes for volatile data
            static: options.staticTTL || 7 * 24 * 60 * 60 * 1000 // 7 days for static content
        };
        
        // Cache statistics
        this.stats = {
            hits: 0,
            misses: 0,
            writes: 0,
            deletes: 0,
            errors: 0
        };
        
        // Universal placeholder types (from batch generation)
        this.universalPlaceholders = new Set([
            'market_overview',
            'economic_indicators',
            'technical_analysis',
            'sector_analysis',
            'market_news',
            'market_sentiment'
        ]);
        
        // Client-specific placeholder types
        this.clientPlaceholders = new Set([
            'portfolio_summary',
            'investment_insights',
            'action_items',
            'tax_considerations',
            'goal_progress',
            'alerts',
            'performance'
        ]);
    }

    /**
     * Get content from cache with intelligent routing
     * @param {string} placeholderType - Type of placeholder
     * @param {Object} context - Request context
     * @returns {Object|null} Cached content or null
     */
    async getContent(placeholderType, context = {}) {
        const startTime = Date.now();
        
        try {
            // Determine cache type based on placeholder
            const category = this.getPlaceholderCategory(placeholderType);
            
            if (this.isUniversal(placeholderType)) {
                // Fetch from S3 batch cache
                const content = await this.getBatchContent(placeholderType, context);
                this.recordMetric('CacheGet', 'batch', Date.now() - startTime);
                return content;
            } else {
                // Fetch from DynamoDB client cache
                const cacheKey = this.generateClientCacheKey(context.clientId, placeholderType, context);
                const content = await this.getClientContent(cacheKey, context);
                this.recordMetric('CacheGet', 'client', Date.now() - startTime);
                return content;
            }
        } catch (error) {
            this.logger.error('Cache get error:', error);
            this.stats.errors++;
            this.recordMetric('CacheError', 'get', 1);
            return null;
        }
    }

    /**
     * Set content in cache
     * @param {string} placeholderType - Type of placeholder
     * @param {Object} content - Content to cache
     * @param {Object} context - Request context
     */
    async setContent(placeholderType, content, context = {}) {
        const startTime = Date.now();
        
        try {
            if (this.isUniversal(placeholderType)) {
                await this.setBatchContent(placeholderType, content, context);
                this.recordMetric('CacheSet', 'batch', Date.now() - startTime);
            } else {
                const cacheKey = this.generateClientCacheKey(context.clientId, placeholderType, context);
                await this.setClientContent(cacheKey, content, context);
                this.recordMetric('CacheSet', 'client', Date.now() - startTime);
            }
            
            this.stats.writes++;
        } catch (error) {
            this.logger.error('Cache set error:', error);
            this.stats.errors++;
            this.recordMetric('CacheError', 'set', 1);
        }
    }

    /**
     * Get batch content from S3
     */
    async getBatchContent(placeholderType, context) {
        // Return null if S3 is not configured (browser environment)
        if (!s3) {
            this.stats.misses++;
            return null;
        }

        const key = this.generateBatchCacheKey(placeholderType, context);

        try {
            const params = {
                Bucket: this.s3Bucket,
                Key: key
            };

            const response = await s3.getObject(params).promise();
            const content = JSON.parse(response.Body.toString());

            // Check if content is stale
            if (this.isBatchStale(content, context)) {
                this.stats.misses++;
                return null;
            }

            this.stats.hits++;
            return content;
        } catch (error) {
            if (error.code === 'NoSuchKey') {
                this.stats.misses++;
                return null;
            }
            return createCacheError('Failed to retrieve content from cache', error);
        }
    }

    /**
     * Set batch content in S3
     */
    async setBatchContent(placeholderType, content, context) {
        // Skip if S3 is not configured (browser environment)
        if (!s3) {
            this.logger.warn('S3 not available in browser environment - cache write skipped');
            return;
        }

        const key = this.generateBatchCacheKey(placeholderType, context);

        const enrichedContent = {
            ...content,
            _cache: {
                timestamp: Date.now(),
                ttl: this.ttl.batch,
                scheduleType: context.scheduleType,
                version: context.version || '1.0'
            }
        };

        const params = {
            Bucket: this.s3Bucket,
            Key: key,
            Body: JSON.stringify(enrichedContent),
            ContentType: 'application/json',
            Metadata: {
                'cache-control': `max-age=${Math.floor(this.ttl.batch / 1000)}`,
                'placeholder-type': placeholderType,
                'schedule-type': context.scheduleType || 'default'
            }
        };

        await s3.putObject(params).promise();
    }

    /**
     * Get client content from DynamoDB
     */
    async getClientContent(cacheKey, context) {
        // Return null if DynamoDB is not configured (browser environment)
        if (!dynamodb) {
            this.stats.misses++;
            return null;
        }

        const params = {
            TableName: this.clientCacheTable,
            Key: { cacheKey }
        };

        try {
            const result = await dynamodb.get(params).promise();

            if (!result.Item) {
                this.stats.misses++;
                return null;
            }

            const content = result.Item;

            // Check if content is stale
            if (this.isClientStale(content, context)) {
                // Delete stale entry
                await this.deleteClientContent(cacheKey);
                this.stats.misses++;
                return null;
            }

            this.stats.hits++;
            return content.data;
        } catch (error) {
            this.logger.error('DynamoDB get error:', error);
            return null;
        }
    }

    /**
     * Set client content in DynamoDB
     */
    async setClientContent(cacheKey, content, context) {
        // Skip if DynamoDB is not configured (browser environment)
        if (!dynamodb) {
            this.logger.warn('DynamoDB not available in browser environment - cache write skipped');
            return;
        }

        const ttlSeconds = Math.floor(Date.now() / 1000) + Math.floor(this.ttl.client / 1000);

        const item = {
            cacheKey,
            data: content,
            clientId: context.clientId,
            portfolioHash: context.portfolioHash,
            preferencesHash: context.preferencesHash,
            timestamp: Date.now(),
            ttl: ttlSeconds,
            version: context.version || '1.0'
        };

        const params = {
            TableName: this.clientCacheTable,
            Item: item
        };

        await dynamodb.put(params).promise();
    }

    /**
     * Delete client content from DynamoDB
     */
    async deleteClientContent(cacheKey) {
        // Skip if DynamoDB is not configured (browser environment)
        if (!dynamodb) {
            return;
        }

        const params = {
            TableName: this.clientCacheTable,
            Key: { cacheKey }
        };

        await dynamodb.delete(params).promise();
        this.stats.deletes++;
    }

    /**
     * Generate cache key for batch content
     */
    generateBatchCacheKey(placeholderType, context) {
        const scheduleType = context.scheduleType || 'default';
        const date = context.date || new Date().toISOString().split('T')[0];
        return `${this.batchPrefix}${scheduleType}/${date}/${placeholderType}.json`;
    }

    /**
     * Generate cache key for client content
     */
    generateClientCacheKey(clientId, placeholderType, context) {
        const components = [
            clientId,
            placeholderType,
            context.portfolioHash || 'default',
            context.preferencesHash || 'default'
        ];
        
        return `client:${components.join(':')}`;
    }

    /**
     * Check if batch content is stale
     */
    isBatchStale(content, context) {
        if (!content._cache) return true;
        
        const age = Date.now() - content._cache.timestamp;
        
        // Check TTL
        if (age > this.ttl.batch) return true;
        
        // Check for market events that require refresh
        if (context.marketEvent === 'significant') {
            // Reduce TTL for volatile market conditions
            if (age > this.ttl.market) return true;
        }
        
        return false;
    }

    /**
     * Check if client content is stale
     */
    isClientStale(content, context) {
        // Check TTL (DynamoDB TTL handles this automatically, but we double-check)
        if (content.ttl && content.ttl < Math.floor(Date.now() / 1000)) {
            return true;
        }
        
        // Check if portfolio changed
        if (context.portfolioHash && content.portfolioHash !== context.portfolioHash) {
            return true;
        }
        
        // Check if preferences changed
        if (context.preferencesHash && content.preferencesHash !== context.preferencesHash) {
            return true;
        }
        
        // Check age
        const age = Date.now() - content.timestamp;
        if (age > this.ttl.client) {
            return true;
        }
        
        // Force refresh if requested
        if (context.forceRefresh) {
            return true;
        }
        
        return false;
    }

    /**
     * Determine if placeholder is universal (batch) or client-specific
     */
    isUniversal(placeholderType) {
        const category = placeholderType.split('.')[0];
        return this.universalPlaceholders.has(category);
    }

    /**
     * Get placeholder category
     */
    getPlaceholderCategory(placeholderType) {
        return placeholderType.split('.')[0];
    }

    /**
     * Invalidate cache for specific patterns
     */
    async invalidate(pattern, context = {}) {
        this.logger.info(`Invalidating cache for pattern: ${pattern}`);
        
        if (pattern.startsWith('client:')) {
            // Invalidate client cache
            const clientId = pattern.split(':')[1] || context.clientId;
            await this.invalidateClientCache(clientId);
        } else if (pattern.startsWith('batch:')) {
            // Invalidate batch cache
            const scheduleType = pattern.split(':')[1] || context.scheduleType;
            await this.invalidateBatchCache(scheduleType);
        } else if (pattern === '*') {
            // Clear all caches
            await this.clearAll();
        }
    }

    /**
     * Invalidate all cache entries for a client
     */
    async invalidateClientCache(clientId) {
        // Skip if DynamoDB is not configured (browser environment)
        if (!dynamodb) {
            this.logger.warn('DynamoDB not available - cannot invalidate client cache');
            return;
        }

        // Query all cache entries for the client
        const params = {
            TableName: this.clientCacheTable,
            FilterExpression: 'clientId = :clientId',
            ExpressionAttributeValues: {
                ':clientId': clientId
            }
        };

        try {
            const result = await dynamodb.scan(params).promise();

            // Delete each entry
            for (const item of result.Items) {
                await this.deleteClientContent(item.cacheKey);
            }

            this.logger.info(`Invalidated ${result.Items.length} cache entries for client: ${clientId}`);
        } catch (error) {
            this.logger.error('Error invalidating client cache:', error);
        }
    }

    /**
     * Invalidate batch cache for a schedule type
     */
    async invalidateBatchCache(scheduleType) {
        // Skip if S3 is not configured (browser environment)
        if (!s3) {
            this.logger.warn('S3 not available - cannot invalidate batch cache');
            return;
        }

        const prefix = `${this.batchPrefix}${scheduleType}/`;

        const params = {
            Bucket: this.s3Bucket,
            Prefix: prefix
        };

        try {
            const objects = await s3.listObjectsV2(params).promise();

            if (objects.Contents && objects.Contents.length > 0) {
                const deleteParams = {
                    Bucket: this.s3Bucket,
                    Delete: {
                        Objects: objects.Contents.map(obj => ({ Key: obj.Key }))
                    }
                };

                await s3.deleteObjects(deleteParams).promise();
                this.logger.info(`Invalidated ${objects.Contents.length} batch cache entries for: ${scheduleType}`);
            }
        } catch (error) {
            this.logger.error('Error invalidating batch cache:', error);
        }
    }

    /**
     * Clear all caches
     */
    async clearAll() {
        await this.invalidateBatchCache('*');
        // Note: Clearing all DynamoDB entries should be done carefully
        this.logger.info('All caches cleared');
        this.resetStats();
    }

    /**
     * Get cache statistics
     */
    getStats() {
        const total = this.stats.hits + this.stats.misses;
        const hitRate = total > 0 ? (this.stats.hits / total * 100).toFixed(2) : 0;
        
        return {
            ...this.stats,
            hitRate: `${hitRate}%`,
            total
        };
    }

    /**
     * Reset statistics
     */
    resetStats() {
        this.stats = {
            hits: 0,
            misses: 0,
            writes: 0,
            deletes: 0,
            errors: 0
        };
    }

    /**
     * Record metric to CloudWatch
     */
    async recordMetric(metricName, dimension, value) {
        // Skip if CloudWatch is not configured (browser environment)
        if (!cloudwatch) {
            return;
        }

        try {
            await cloudwatch.putMetricData({
                Namespace: 'NewsletterCache',
                MetricData: [{
                    MetricName: metricName,
                    Value: value,
                    Unit: metricName.includes('Time') ? 'Milliseconds' : 'Count',
                    Timestamp: new Date(),
                    Dimensions: [{
                        Name: 'CacheType',
                        Value: dimension
                    }]
                }]
            }).promise();
        } catch (error) {
            // Don't let metrics errors affect cache operations
            this.logger.error('Metric recording error:', error);
        }
    }

    /**
     * Warm up cache with preloaded content
     */
    async warmUp(placeholders, context) {
        this.logger.info('Warming up cache...');
        const results = [];
        
        for (const placeholder of placeholders) {
            try {
                const content = await this.getContent(placeholder, context);
                if (!content) {
                    results.push({ placeholder, status: 'miss' });
                } else {
                    results.push({ placeholder, status: 'hit' });
                }
            } catch (error) {
                results.push({ placeholder, status: 'error', error: error.message });
            }
        }
        
        const hits = results.filter(r => r.status === 'hit').length;
        this.logger.info(`Cache warm-up complete: ${hits}/${placeholders.length} hits`);
        
        return results;
    }
}

export default HybridCache;
